/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  FilteredGuideService,
  TsriServiceEndpointService
} from "@tivocorp/data-domain-lib";
import type {
  GuideMenuItemV2,
  GuideProgramBucketListV2,
  GuideProgramListV2,
  TFilteredGuideServiceFetchGuideMenuItemsParams,
  TFilteredGuideServiceFetchGuideProgramBucketsParams,
  TFilteredGuideServiceFetchGuideProgramsParams,
  TFilteredGuideServiceFetchGuideChannelIdsParams
} from "@tivocorp/data-domain-lib";

import type { IFilteredGuideInteractor } from "../../interfaces";
import type { TNetworkOptions } from "../../types";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor, ServiceLocator } from "../base";

/**
 * Atomic interactor wrapping {@link FilteredGuideService} for the
 * Filtered Guide feature.
 *
 * Stateless — a fresh `FilteredGuideService` is built per call via
 * {@link ServiceLocator}. The {@link ApplicationFeatureArea} is fixed at
 * construction time so the `applicationFeatureArea` request header matches
 * the calling feature.
 */
export class FilteredGuideInteractor
  extends BaseInteractor
  implements IFilteredGuideInteractor
{
  private static readonly TAG: string = "FilteredGuideInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the request
   *                       header for every call made by this instance.
   *                       Defaults to {@link ApplicationFeatureArea.ALL}.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.GUIDE
  ) {
    super();
  }

  /**
   * @inheritdoc
   */
  public async fetchGuideMenuItems(
    params: TFilteredGuideServiceFetchGuideMenuItemsParams,
    options?: TNetworkOptions
  ): Promise<Array<GuideMenuItemV2>> {
    return this.executeGuarded(
      "fetchGuideMenuItems",
      async (guardedSignal: AbortSignal): Promise<Array<GuideMenuItemV2>> => {
        Log.d(FilteredGuideInteractor.TAG, "fetchGuideMenuItems() called");
        const service: FilteredGuideService = ServiceLocator.getInstance(
          FilteredGuideService,
          await this.buildServicesParams(
            TIVO_SERVICES.FilteredGuideService,
            this.mFeatureArea
          )
        );
        return service.fetchGuideMenuItems(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchGuideProgramBuckets(
    params: TFilteredGuideServiceFetchGuideProgramBucketsParams,
    options?: TNetworkOptions
  ): Promise<GuideProgramBucketListV2> {
    return this.executeGuarded(
      "fetchGuideProgramBuckets",
      async (guardedSignal: AbortSignal): Promise<GuideProgramBucketListV2> => {
        Log.d(FilteredGuideInteractor.TAG, "fetchGuideProgramBuckets() called");
        const service: FilteredGuideService = ServiceLocator.getInstance(
          FilteredGuideService,
          await this.buildServicesParams(
            TIVO_SERVICES.FilteredGuideService,
            this.mFeatureArea
          )
        );
        return service.fetchGuideProgramBuckets(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchGuidePrograms(
    params: TFilteredGuideServiceFetchGuideProgramsParams,
    options?: TNetworkOptions
  ): Promise<GuideProgramListV2> {
    return this.executeGuarded(
      "fetchGuidePrograms",
      async (guardedSignal: AbortSignal): Promise<GuideProgramListV2> => {
        Log.d(FilteredGuideInteractor.TAG, "fetchGuidePrograms() called");
        const service: FilteredGuideService = ServiceLocator.getInstance(
          FilteredGuideService,
          await this.buildServicesParams(
            TIVO_SERVICES.FilteredGuideService,
            this.mFeatureArea
          )
        );
        return service.fetchGuidePrograms(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchGuideChannelIds(
    params: TFilteredGuideServiceFetchGuideChannelIdsParams,
    options?: TNetworkOptions
  ): Promise<Array<string>> {
    return this.executeGuarded(
      "fetchGuideChannelIds",
      async (guardedSignal: AbortSignal): Promise<Array<string>> => {
        Log.d(FilteredGuideInteractor.TAG, "fetchGuideChannelIds() called");
        const service: FilteredGuideService = ServiceLocator.getInstance(
          FilteredGuideService,
          await this.buildServicesParams(
            TIVO_SERVICES.FilteredGuideService,
            this.mFeatureArea
          )
        );
        return service.fetchGuideChannelIds(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Builds the icon URL for a guide filter menu item.
   *
   * Returns `null` when:  `menuItemId` is blank or empty.
   *
   * @param menuItemId - Opaque server ID for the filter menu item (e.g. "tivo:fgmi.1").
   * @param isFocused  - `true` to request the focused-state icon variant.
   * @returns Fully-qualified icon URL, or `null` if it cannot be resolved.
   */
  public static buildFilterIconUrl(
    menuItemId: string,
    isFocused: boolean,
    height: number = 96,
    width: number = 96
  ): string | null {
    if (menuItemId.length === 0) return null;

    const baseUrl = TsriServiceEndpointService.getServiceEndpointByServiceName(
      TIVO_SERVICES.ImageHost
    );

    const params: URLSearchParams = new URLSearchParams({
      objectid: menuItemId,
      imagetype: isFocused ? "guideFilterFocusedIcon" : "guideFilterIcon",
      width: String(width),
      height: String(height)
    });

    return `${baseUrl}/images?${params.toString()}`;
  }
}
